import { InternalRoleSchema, Permissions, RoleSchema } from '@buyerflo/core/schemas/roles'
import { FrontendUserSchema } from '@buyerflo/core/schemas/userMembership'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation } from '@tanstack/react-query'
import { useRolesApi, useUsersApi } from 'apiProviders'
import { DeleteRounded } from 'components/icons'
import { SlotInput } from 'components/input'
import { useShowSnack } from 'components/snackbar'
import { prop } from 'ramda'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import Button from 'components/button'
import { Dialog, SizableText, Switch, XStack, YStack } from 'tamagui'
import { useTRPC } from 'utils/trpc'

export function EditRoleModal({
    role,
    users,
    close,
}: {
    role: {} | InternalRoleSchema
    close
    users: FrontendUserSchema[]
}) {
    const trpc = useTRPC()
    const rolesApi = useRolesApi()
    const usersApi = useUsersApi()
    const showSnack = useShowSnack()
    const isNew = (role as InternalRoleSchema).id == undefined
    const addRoleMutation = useMutation(trpc.roles.createRole.mutationOptions())
    const updateRoleMutation = useMutation(trpc.roles.updateRole.mutationOptions())
    const deleteRoleMutation = useMutation(trpc.roles.deleteRole.mutationOptions())

    const currentUsers = users
        .filter(user => user.conjunctionalAgencyId == (role as InternalRoleSchema).id)
        .map(prop('userId'))

    const { register, control, handleSubmit, formState, getValues, setValue } = useForm<RoleSchema>({
        defaultValues: role
            ? {
                  rolePermissions: [],
                  ...role,
              }
            : {},
        resolver: zodResolver(RoleSchema),
    })

    if (Object.keys(formState.errors).length > 0) {
        console.log(formState.errors)
    }

    const onSubmit: SubmitHandler<RoleSchema> = async data => {
        try {
            if (isNew) {
                await addRoleMutation.mutateAsync(data)
                close()
            } else {
                await updateRoleMutation.mutateAsync({
                    id: (role as any).id,
                    data,
                })
            }
            await Promise.all([rolesApi.refresh(), usersApi.refresh()])
            showSnack(`Role ${isNew ? 'Added' : 'Saved'}`, 'success')
            close()
        } catch (e) {
            console.error(e)
            showSnack('Error saving role', 'danger')
        }
    }

    return (
        <form
            noValidate
            onSubmit={handleSubmit(onSubmit)}
        >
            <Dialog.Title
                fontWeight="700"
                letterSpacing={-0.3}
            >
                {isNew ? 'Create Role' : 'Edit Role'}
            </Dialog.Title>
            <YStack
                gap="$4"
                minWidth={350}
                paddingTop="$1"
            >
                <Controller
                    name="roleName"
                    control={control}
                    render={field => (
                        <SlotInput
                            {...field}
                            label="Role Name"
                        />
                    )}
                />
                <Controller
                    name="rolePermissions"
                    control={control}
                    render={({ field }) => (
                        <YStack
                            gap="$1"
                            alignItems="flex-start"
                        >
                            {Object.entries(Permissions).map(([key, value]) => (
                                <XStack
                                    key={key}
                                    alignItems="center"
                                    gap="$2"
                                >
                                    <Switch
                                        size="$3"
                                        p={0}
                                        checked={field.value.includes(key as any)}
                                        onCheckedChange={checked => {
                                            field.onChange(
                                                checked
                                                    ? [...field.value, key]
                                                    : field.value.filter(v => v != key),
                                            )
                                        }}
                                    >
                                        <Switch.Thumb animation="quick" />
                                    </Switch>
                                    <SizableText>{value}</SizableText>
                                </XStack>
                            ))}
                        </YStack>
                    )}
                />
            </YStack>
            <XStack
                gap="$2"
                flexDirection="row-reverse"
                marginTop="$4"
            >
                <Button
                    theme="primary"
                    onPress={handleSubmit(onSubmit)}
                    animation="quick"
                >
                    Save
                </Button>
                <Button
                    onPress={close}
                    variant="outlined"
                    animation="quick"
                >
                    Cancel
                </Button>
                {!isNew && (
                    <Button
                        variant="outlined"
                        theme="red"
                        disabled={
                            usersApi.data?.some(user => user.roleId == (role as InternalRoleSchema).id) ??
                            true
                        }
                        icon={<DeleteRounded />}
                        marginRight="auto"
                        onPress={async () => {
                            try {
                                await deleteRoleMutation.mutateAsync({ id: (role as InternalRoleSchema).id })
                                await rolesApi.refresh()
                                showSnack('Role Deleted', 'success')
                                close()
                            } catch (e) {
                                console.error(e)
                                showSnack('Error deleting role', 'danger')
                            }
                        }}
                    >
                        Delete
                    </Button>
                )}
            </XStack>
        </form>
    )
}
